import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Lock,
  LogOut,
  Loader2,
  AlertCircle,
  Droplet,
  Footprints,
  Moon,
  Clock,
  Flame,
  Pill,
  Syringe,
  Plus,
  Check,
  Calendar,
  Weight,
  Activity,
  TrendingUp,
  LineChart as LineChartIcon,
  Download,
  Camera,
  Sparkles,
  X,
  CalendarDays,
  MapPin,
  Users,
  CheckCircle2,
  Flame as FlameStreak,
  FileText,
  ExternalLink,
} from "lucide-react";
import { useClientAuth } from "../hooks/useClientAuth";
import {
  clientLogs,
  clientPostDaily,
  clientPostWeekly,
  clientPostMonthly,
  clientAnalyzeFoodPhoto,
  clientListSessions,
  clientRsvp,
  clientCancelRsvp,
  clientListLabs,
  downloadLab,
} from "../lib/api";
import LineChart from "../components/LineChart";
import LabCommentThread from "../components/LabCommentThread";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function monthStr(d = new Date()) {
  return d.toISOString().slice(0, 7);
}
function mondayOf(d = new Date()) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Mon=0
  date.setDate(date.getDate() - day);
  return date.toISOString().slice(0, 10);
}

/* -------- Login screen -------- */
function LoginScreen({ onLogin, error }) {
  const [identifier, setIdentifier] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onLogin(identifier.trim(), pin);
    setLoading(false);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)] px-5">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[var(--lc-line)] rounded-md p-8 lg:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 leading-none mb-7">
          <span className="font-serif-display text-xl tracking-tight">LONGEV</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
          <span className="font-serif-display italic text-lg">circle</span>
        </Link>
        <div className="w-12 h-12 rounded-full bg-[var(--lc-moss)] grid place-items-center mb-6">
          <Lock className="h-5 w-5 text-white" />
        </div>
        <div className="eyebrow text-[var(--lc-moss)] mb-3">Member Portal</div>
        <h1 className="font-serif-display text-3xl leading-tight">
          Sign in to <span className="italic">track your progress.</span>
        </h1>
        <p className="text-sm text-[var(--lc-muted)] mt-3">
          Use the email (or phone/name) your coach registered, plus your 4-digit PIN.
        </p>

        <div className="mt-7 space-y-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">Email, phone or name</label>
            <input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)]"
              autoFocus
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-2">4-digit PIN</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-base focus:outline-none focus:border-[var(--lc-ink)] tracking-[0.5em] text-center"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          disabled={loading || !identifier || pin.length < 4}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Sign in
        </button>

        <p className="mt-6 text-[11px] text-center text-[var(--lc-muted)] tracking-wide">
          Don't have access yet? Ask your coach to set up your portal account.
        </p>
      </form>
    </section>
  );
}

/* -------- Shell with tabs -------- */
const TABS = [
  { id: "today", label: "Today", icon: Clock },
  { id: "weekly", label: "Weekly", icon: Calendar },
  { id: "monthly", label: "Monthly", icon: TrendingUp },
  { id: "sessions", label: "Sessions", icon: CalendarDays },
  { id: "labs", label: "Labs", icon: FileText },
  { id: "dashboard", label: "Dashboard", icon: LineChartIcon },
];

/* Meal streak: count consecutive days (ending at today or yesterday) with food[] entries */
function computeMealStreak(dailyLogs) {
  if (!dailyLogs?.length) return 0;
  const byDate = new Map();
  for (const d of dailyLogs) {
    if ((d.food || []).length > 0) byDate.set(d.log_date, true);
  }
  if (byDate.size === 0) return 0;
  // Start from the most recent logged day (today or earlier)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let streak = 0;
  // Allow starting from today OR yesterday to not penalize the user before they log today
  for (let offset = 0; offset <= 1; offset += 1) {
    const start = new Date(today);
    start.setDate(start.getDate() - offset);
    const key = start.toISOString().slice(0, 10);
    if (byDate.has(key)) {
      // walk back as long as consecutive days are logged
      let probe = new Date(start);
      while (byDate.has(probe.toISOString().slice(0, 10))) {
        streak += 1;
        probe.setDate(probe.getDate() - 1);
      }
      return streak;
    }
  }
  return 0;
}

/* -------- Daily form -------- */
function TodayForm({ token, logs, onSaved, isT1D }) {
  const streak = useMemo(() => computeMealStreak(logs.daily || []), [logs.daily]);
  const existing = useMemo(
    () => logs.daily?.find((d) => d.log_date === todayStr()),
    [logs.daily]
  );
  const [form, setForm] = useState(() => ({
    log_date: todayStr(),
    water_ml: existing?.water_ml || 0,
    steps: existing?.steps || "",
    sleep_hours: existing?.sleep_hours || "",
    fasting: existing?.fasting || false,
    activity_notes: existing?.activity_notes || "",
    supplements: existing?.supplements || "",
    medications: existing?.medications || "",
    insulin_dose: existing?.insulin_dose || "",
    food: existing?.food || [],
    notes: existing?.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newFood, setNewFood] = useState({ meal: "Breakfast", text: "" });

  useEffect(() => {
    if (existing) {
      setForm({
        log_date: todayStr(),
        water_ml: existing.water_ml || 0,
        steps: existing.steps || "",
        sleep_hours: existing.sleep_hours || "",
        fasting: existing.fasting || false,
        activity_notes: existing.activity_notes || "",
        supplements: existing.supplements || "",
        medications: existing.medications || "",
        insulin_dose: existing.insulin_dose || "",
        food: existing.food || [],
        notes: existing.notes || "",
      });
    }
  }, [existing]);

  const addFood = () => {
    if (!newFood.text.trim()) return;
    setForm((f) => ({ ...f, food: [...f.food, newFood] }));
    setNewFood({ meal: newFood.meal, text: "" });
  };
  const removeFood = (i) =>
    setForm((f) => ({ ...f, food: f.food.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await clientPostDaily(token, {
        log_date: form.log_date,
        water_ml: Number(form.water_ml) || 0,
        steps: Number(form.steps) || 0,
        sleep_hours: form.sleep_hours === "" ? null : Number(form.sleep_hours),
        fasting: !!form.fasting,
        activity_notes: form.activity_notes || null,
        supplements: form.supplements || null,
        medications: form.medications || null,
        insulin_dose: form.insulin_dose === "" ? null : Number(form.insulin_dose),
        food: form.food,
        notes: form.notes || null,
      });
      setSaved(true);
      await onSaved();
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-[var(--lc-muted)]">Daily tracker</div>
          <h2 className="font-serif-display text-3xl leading-tight mt-1">
            {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </h2>
          {streak >= 3 && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--lc-moss)]/15 border border-[var(--lc-moss)]/40 rounded-full"
              data-testid="meal-streak-badge"
              title={`${streak} consecutive days with a food log`}
            >
              <FlameStreak className="h-3 w-3 text-[var(--lc-moss-dark)]" />
              <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-[var(--lc-moss-dark)]">
                {streak}-day meal streak
              </span>
            </div>
          )}
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[11px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
          {saved ? "Saved" : "Save Today"}
        </button>
      </div>

      {/* Water */}
      <Card icon={Droplet} title="Water" subtitle={`${form.water_ml} ml`}>
        <div className="flex flex-wrap gap-2">
          {[250, 500, 750].map((n) => (
            <button
              key={n}
              onClick={() => setForm((f) => ({ ...f, water_ml: f.water_ml + n }))}
              className="px-4 py-2.5 bg-[var(--lc-moss)] text-white text-[12px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
            >
              +{n} ml
            </button>
          ))}
          <button
            onClick={() => setForm((f) => ({ ...f, water_ml: 0 }))}
            className="px-4 py-2.5 bg-white border border-[var(--lc-line)] text-[12px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]"
          >
            Reset
          </button>
        </div>
      </Card>

      {/* Steps / sleep / fasting */}
      <Card icon={Activity} title="Movement & recovery">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Steps today" icon={Footprints}>
            <NumInput value={form.steps} onChange={(v) => setForm((f) => ({ ...f, steps: v }))} placeholder="e.g. 8500" />
          </Field>
          <Field label="Sleep hours" icon={Moon}>
            <NumInput value={form.sleep_hours} onChange={(v) => setForm((f) => ({ ...f, sleep_hours: v }))} placeholder="e.g. 7.5" step="0.1" />
          </Field>
          <Field label="Fasting day?" icon={Flame}>
            <Toggle value={form.fasting} onChange={(v) => setForm((f) => ({ ...f, fasting: v }))} />
          </Field>
        </div>
        <div className="mt-4">
          <TextInput
            label="Activity notes (gym, walk, swim...)"
            value={form.activity_notes}
            onChange={(v) => setForm((f) => ({ ...f, activity_notes: v }))}
          />
        </div>
      </Card>

      {/* Food log */}
      <FoodLogCard
        token={token}
        form={form}
        setForm={setForm}
        newFood={newFood}
        setNewFood={setNewFood}
        addFood={addFood}
        removeFood={removeFood}
      />

      {/* Supplements / medications / insulin */}
      <Card icon={Pill} title="Supplements & medications">
        <div className="grid sm:grid-cols-2 gap-4">
          <TextInput
            label="Supplements taken today"
            value={form.supplements}
            onChange={(v) => setForm((f) => ({ ...f, supplements: v }))}
            placeholder="Vit D, Omega-3..."
          />
          <TextInput
            label="Medications taken today"
            value={form.medications}
            onChange={(v) => setForm((f) => ({ ...f, medications: v }))}
            placeholder="Name + dose"
          />
          {isT1D && (
            <Field label="Insulin dose (units)" icon={Syringe}>
              <NumInput
                value={form.insulin_dose}
                onChange={(v) => setForm((f) => ({ ...f, insulin_dose: v }))}
                placeholder="Total units today"
                step="0.5"
              />
            </Field>
          )}
        </div>
        <div className="mt-4">
          <TextInput
            label="Anything else to note?"
            value={form.notes}
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
          />
        </div>
      </Card>
    </div>
  );
}

/* -------- Food log card (with AI photo analysis) -------- */
function FoodLogCard({ token, form, setForm, newFood, setNewFood, addFood, removeFood }) {
  const fileInputRef = useRef(null);
  const [photoState, setPhotoState] = useState({
    busy: false,
    error: "",
    previewUrl: "",
    base64: "",
    mimeType: "",
    result: null,
  });

  const resetPhoto = () => {
    if (photoState.previewUrl) URL.revokeObjectURL(photoState.previewUrl);
    setPhotoState({ busy: false, error: "", previewUrl: "", base64: "", mimeType: "", result: null });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      setPhotoState((s) => ({ ...s, error: "Please pick a JPEG, PNG or WEBP image." }));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setPhotoState((s) => ({ ...s, error: "Image is too large (max 8 MB)." }));
      return;
    }
    // Downscale large images on-device before upload, for speed and lower cost
    const { base64, previewUrl, mimeType } = await resizeAndEncode(file);
    setPhotoState({
      busy: false,
      error: "",
      previewUrl,
      base64,
      mimeType,
      result: null,
    });
  };

  const analyze = async () => {
    if (!photoState.base64) return;
    setPhotoState((s) => ({ ...s, busy: true, error: "", result: null }));
    try {
      const result = await clientAnalyzeFoodPhoto(token, {
        image_base64: photoState.base64,
        mime_type: photoState.mimeType === "image/png" ? "image/png" : photoState.mimeType === "image/webp" ? "image/webp" : "image/jpeg",
        meal: newFood.meal,
        note: newFood.text || undefined,
      });
      setPhotoState((s) => ({ ...s, busy: false, result }));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Analysis failed";
      setPhotoState((s) => ({ ...s, busy: false, error: typeof detail === "string" ? detail : "Analysis failed" }));
    }
  };

  const addFromAI = () => {
    if (!photoState.result) return;
    const r = photoState.result;
    setForm((f) => ({
      ...f,
      food: [
        ...f.food,
        {
          meal: newFood.meal,
          text: r.description || "Meal",
          items: r.items || [],
          calories: r.calories || 0,
          protein_g: r.protein_g || 0,
          carbs_g: r.carbs_g || 0,
          fat_g: r.fat_g || 0,
          confidence: r.confidence || "medium",
          source: "ai_photo",
        },
      ],
    }));
    setNewFood((n) => ({ ...n, text: "" }));
    resetPhoto();
  };

  const macroTotals = form.food.reduce(
    (acc, f) => ({
      cal: acc.cal + (Number(f.calories) || 0),
      p: acc.p + (Number(f.protein_g) || 0),
      c: acc.c + (Number(f.carbs_g) || 0),
      ft: acc.ft + (Number(f.fat_g) || 0),
    }),
    { cal: 0, p: 0, c: 0, ft: 0 }
  );

  return (
    <Card
      icon={Flame}
      title="Food log"
      subtitle={`${form.food.length} ${form.food.length === 1 ? "entry" : "entries"}`}
    >
      {/* Daily macro totals strip */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <MacroPill label="kcal" value={macroTotals.cal || "—"} />
        <MacroPill label="protein" value={macroTotals.p ? `${macroTotals.p} g` : "—"} />
        <MacroPill label="carbs" value={macroTotals.c ? `${macroTotals.c} g` : "—"} />
        <MacroPill label="fat" value={macroTotals.ft ? `${macroTotals.ft} g` : "—"} />
      </div>

      {/* Add row (meal + text + Add + Photo) */}
      <div className="grid sm:grid-cols-[150px_1fr_auto_auto] gap-2">
        <select
          value={newFood.meal}
          onChange={(e) => setNewFood((f) => ({ ...f, meal: e.target.value }))}
          className="px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
          data-testid="food-meal-select"
        >
          {["Breakfast", "Lunch", "Dinner", "Snack"].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <input
          value={newFood.text}
          onChange={(e) => setNewFood((f) => ({ ...f, text: e.target.value }))}
          placeholder="2 eggs, 1 banana, oats..."
          className="px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFood())}
          data-testid="food-text-input"
        />
        <button
          onClick={addFood}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
          data-testid="food-add-button"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-[var(--lc-ink)] text-[var(--lc-ink)] text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-ink)] hover:text-white transition-colors"
          data-testid="food-photo-button"
          title="Snap or upload a meal photo — AI will estimate the macros"
        >
          <Camera className="h-3.5 w-3.5" /> Photo
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={onFileSelected}
          className="hidden"
          data-testid="food-photo-input"
        />
      </div>

      {/* AI photo preview + analyse panel */}
      {photoState.previewUrl && (
        <div className="mt-4 border border-[var(--lc-line)] rounded-md p-4 bg-[var(--lc-cream)]/60" data-testid="food-ai-panel">
          <div className="flex gap-4">
            <img
              src={photoState.previewUrl}
              alt="meal preview"
              className="w-28 h-28 lg:w-32 lg:h-32 object-cover rounded-sm border border-[var(--lc-line)] flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="eyebrow text-[var(--lc-moss)] flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AI macro estimate
                  </div>
                  <div className="font-serif-display text-xl mt-0.5">
                    {photoState.result?.description || "Ready to analyze."}
                  </div>
                </div>
                <button
                  onClick={resetPhoto}
                  className="text-[var(--lc-muted)] hover:text-red-700"
                  title="Remove"
                  data-testid="food-ai-reset"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {!photoState.result && !photoState.busy && !photoState.error && (
                <div className="mt-3">
                  <button
                    onClick={analyze}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--lc-moss)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
                    data-testid="food-ai-analyze"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Analyze with AI
                  </button>
                  <p className="text-[11px] text-[var(--lc-muted)] mt-2">
                    AI estimate — not a substitute for a kitchen scale.
                  </p>
                </div>
              )}

              {photoState.busy && (
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-[var(--lc-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Reading your plate...
                </div>
              )}

              {photoState.error && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm" data-testid="food-ai-error">
                  <AlertCircle className="h-3.5 w-3.5" /> {photoState.error}
                </div>
              )}

              {photoState.result && (
                <div className="mt-3" data-testid="food-ai-result">
                  <div className="grid grid-cols-4 gap-2">
                    <MacroPill label="kcal" value={photoState.result.calories} />
                    <MacroPill label="protein" value={`${photoState.result.protein_g} g`} />
                    <MacroPill label="carbs" value={`${photoState.result.carbs_g} g`} />
                    <MacroPill label="fat" value={`${photoState.result.fat_g} g`} />
                  </div>
                  {photoState.result.items?.length > 0 && (
                    <div className="text-xs text-[var(--lc-muted)] mt-2">
                      Items: {photoState.result.items.join(", ")}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={addFromAI}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
                      data-testid="food-ai-add"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add to food log
                    </button>
                    <button
                      onClick={analyze}
                      className="px-4 py-2.5 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]"
                      data-testid="food-ai-retry"
                    >
                      Re-analyze
                    </button>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--lc-muted)]">
                      Confidence · {photoState.result.confidence}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {form.food.length > 0 && (
        <ul className="mt-4 divide-y divide-[var(--lc-line)] border-t border-[var(--lc-line)]">
          {form.food.map((f, i) => (
            <li key={i} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`food-entry-${i}`}>
              <div className="min-w-0">
                <span className="eyebrow text-[var(--lc-moss)] mr-2">{f.meal}</span>
                <span>{f.text}</span>
                {(f.calories > 0 || f.protein_g > 0) && (
                  <span className="ml-2 text-[11px] text-[var(--lc-muted)]">
                    · {f.calories || 0} kcal · P {f.protein_g || 0} · C {f.carbs_g || 0} · F {f.fat_g || 0}
                    {f.source === "ai_photo" && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[var(--lc-moss)]">
                        <Sparkles className="h-2.5 w-2.5" /> AI
                      </span>
                    )}
                  </span>
                )}
              </div>
              <button onClick={() => removeFood(i)} className="text-xs text-[var(--lc-muted)] hover:text-red-700" data-testid={`food-entry-remove-${i}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-[11px] text-[var(--lc-muted)]">
        Tip: snap your plate before you eat for the best macro estimate — your coach can review and edit later.
      </p>
    </Card>
  );
}

function MacroPill({ label, value }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-sm px-3 py-2 text-center">
      <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--lc-muted)]">{label}</div>
      <div className="font-serif-display text-base leading-tight mt-0.5">{value}</div>
    </div>
  );
}

/**
 * Resize an image file to max 1280px on the long edge, encode as JPEG base64.
 * Returns { base64, previewUrl, mimeType }.
 */
async function resizeAndEncode(file) {
  const previewUrl = URL.createObjectURL(file);
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = previewUrl;
  });
  const MAX = 1280;
  let { width, height } = img;
  if (width > MAX || height > MAX) {
    const scale = MAX / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
  const base64 = dataUrl.split(",")[1];
  return { base64, previewUrl, mimeType: "image/jpeg" };
}

/* -------- Weekly form -------- */
function WeeklyForm({ token, logs, onSaved }) {
  const weekStart = mondayOf();
  const existing = logs.weekly?.find((w) => w.week_start === weekStart);
  const [form, setForm] = useState({
    week_start: weekStart,
    weight_kg: existing?.weight_kg || "",
    hba1c: existing?.hba1c || "",
    grip_left: existing?.grip_left || "",
    grip_right: existing?.grip_right || "",
    sauna_min: existing?.sauna_min || "",
    massage_min: existing?.massage_min || "",
    exercise_notes: existing?.exercise_notes || "",
    calories_in: existing?.calories_in || "",
    calories_out: existing?.calories_out || "",
    notes: existing?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) {
      const normalized = Object.fromEntries(
        Object.entries(existing).map(([k, v]) => [k, v == null ? "" : v])
      );
      setForm((f) => ({ ...f, ...normalized }));
    }
  }, [existing]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        week_start: form.week_start,
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
        hba1c: form.hba1c === "" ? null : Number(form.hba1c),
        grip_left: form.grip_left === "" ? null : Number(form.grip_left),
        grip_right: form.grip_right === "" ? null : Number(form.grip_right),
        sauna_min: form.sauna_min === "" ? null : Number(form.sauna_min),
        massage_min: form.massage_min === "" ? null : Number(form.massage_min),
        exercise_notes: form.exercise_notes || null,
        calories_in: form.calories_in === "" ? null : Number(form.calories_in),
        calories_out: form.calories_out === "" ? null : Number(form.calories_out),
        notes: form.notes || null,
      };
      await clientPostWeekly(token, payload);
      setSaved(true);
      await onSaved();
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const deficit = (Number(form.calories_in) || 0) - (Number(form.calories_out) || 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-[var(--lc-muted)]">Weekly tracker</div>
          <h2 className="font-serif-display text-3xl leading-tight mt-1">
            Week of {new Date(weekStart).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
          </h2>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[11px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
          {saved ? "Saved" : "Save Week"}
        </button>
      </div>

      <Card icon={Weight} title="Body & bloodwork">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Weight (kg)"><NumInput value={form.weight_kg} onChange={(v) => setForm((f) => ({ ...f, weight_kg: v }))} step="0.1" /></Field>
          <Field label="HbA1c (%)"><NumInput value={form.hba1c} onChange={(v) => setForm((f) => ({ ...f, hba1c: v }))} step="0.1" /></Field>
          <Field label="Grip L (kg) / R (kg)">
            <div className="grid grid-cols-2 gap-2">
              <NumInput value={form.grip_left} onChange={(v) => setForm((f) => ({ ...f, grip_left: v }))} placeholder="L" />
              <NumInput value={form.grip_right} onChange={(v) => setForm((f) => ({ ...f, grip_right: v }))} placeholder="R" />
            </div>
          </Field>
        </div>
      </Card>

      <Card icon={Flame} title="Calories">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Total intake (kcal)"><NumInput value={form.calories_in} onChange={(v) => setForm((f) => ({ ...f, calories_in: v }))} /></Field>
          <Field label="Total burnt (kcal)"><NumInput value={form.calories_out} onChange={(v) => setForm((f) => ({ ...f, calories_out: v }))} /></Field>
          <Field label="Weekly deficit">
            <div className="px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm font-semibold">
              {deficit <= 0 ? deficit : `+${deficit}`} kcal
            </div>
          </Field>
        </div>
      </Card>

      <Card icon={Activity} title="Training & recovery">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sauna this week (min)"><NumInput value={form.sauna_min} onChange={(v) => setForm((f) => ({ ...f, sauna_min: v }))} /></Field>
          <Field label="Massage this week (min)"><NumInput value={form.massage_min} onChange={(v) => setForm((f) => ({ ...f, massage_min: v }))} /></Field>
        </div>
        <div className="mt-4 grid gap-4">
          <TextInput label="Exercise progression (lifts, PRs, mobility wins)" value={form.exercise_notes} onChange={(v) => setForm((f) => ({ ...f, exercise_notes: v }))} />
          <TextInput label="Anything else?" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
        </div>
      </Card>
    </div>
  );
}

/* -------- Monthly form -------- */
function MonthlyForm({ token, logs, onSaved }) {
  const month = monthStr();
  const existing = logs.monthly?.find((m) => m.month === month);
  const [form, setForm] = useState({
    month,
    body_fat_pct: existing?.body_fat_pct || "",
    muscle_mass_kg: existing?.muscle_mass_kg || "",
    hba1c: existing?.hba1c || "",
    notes: existing?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) {
      const normalized = Object.fromEntries(
        Object.entries(existing).map(([k, v]) => [k, v == null ? "" : v])
      );
      setForm((f) => ({ ...f, ...normalized }));
    }
  }, [existing]);

  const save = async () => {
    setSaving(true);
    try {
      await clientPostMonthly(token, {
        month: form.month,
        body_fat_pct: form.body_fat_pct === "" ? null : Number(form.body_fat_pct),
        muscle_mass_kg: form.muscle_mass_kg === "" ? null : Number(form.muscle_mass_kg),
        hba1c: form.hba1c === "" ? null : Number(form.hba1c),
        notes: form.notes || null,
      });
      setSaved(true);
      await onSaved();
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-[var(--lc-muted)]">Monthly tracker</div>
          <h2 className="font-serif-display text-3xl leading-tight mt-1">
            {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
          </h2>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[11px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
          {saved ? "Saved" : "Save Month"}
        </button>
      </div>
      <Card icon={TrendingUp} title="Body composition">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Body fat %"><NumInput value={form.body_fat_pct} onChange={(v) => setForm((f) => ({ ...f, body_fat_pct: v }))} step="0.1" /></Field>
          <Field label="Muscle mass (kg)"><NumInput value={form.muscle_mass_kg} onChange={(v) => setForm((f) => ({ ...f, muscle_mass_kg: v }))} step="0.1" /></Field>
          <Field label="HbA1c (%)"><NumInput value={form.hba1c} onChange={(v) => setForm((f) => ({ ...f, hba1c: v }))} step="0.1" /></Field>
        </div>
        <div className="mt-4">
          <TextInput label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
        </div>
      </Card>
    </div>
  );
}

/* -------- Dashboard -------- */
function Dashboard({ logs }) {
  const daily = (logs.daily || []).slice().reverse();
  const weekly = (logs.weekly || []).slice().reverse();
  const monthly = (logs.monthly || []).slice().reverse();

  const steps = daily.map((d) => ({ x: d.log_date.slice(5), y: d.steps || null }));
  const water = daily.map((d) => ({ x: d.log_date.slice(5), y: d.water_ml || null }));
  const sleep = daily.map((d) => ({ x: d.log_date.slice(5), y: d.sleep_hours ?? null }));
  const insulin = daily.map((d) => ({ x: d.log_date.slice(5), y: d.insulin_dose ?? null }));
  const weight = weekly.map((w) => ({ x: w.week_start.slice(5), y: w.weight_kg ?? null }));
  const hba1c = weekly.map((w) => ({ x: w.week_start.slice(5), y: w.hba1c ?? null }));
  const bodyFat = monthly.map((m) => ({ x: m.month.slice(2), y: m.body_fat_pct ?? null }));
  const muscle = monthly.map((m) => ({ x: m.month.slice(2), y: m.muscle_mass_kg ?? null }));

  const totalWater = daily.reduce((s, d) => s + (d.water_ml || 0), 0);
  const totalSteps = daily.reduce((s, d) => s + (d.steps || 0), 0);
  const avgSleep = (() => {
    const vals = daily.map((d) => d.sleep_hours).filter((x) => x != null);
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  })();
  const fastingDays = daily.filter((d) => d.fasting).length;

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow text-[var(--lc-muted)]">Your dashboard</div>
        <h2 className="font-serif-display text-3xl leading-tight mt-1">
          Progress <span className="italic">at a glance.</span>
        </h2>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Days logged" value={daily.length} />
        <Kpi label="Avg sleep" value={avgSleep ? `${avgSleep} h` : "—"} />
        <Kpi label="Total steps" value={totalSteps.toLocaleString()} />
        <Kpi label="Fasting days" value={fastingDays} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Weight (weekly)" unit="kg">
          <LineChart data={weight} unit="kg" color="#3E5A7A" />
        </ChartCard>
        <ChartCard title="HbA1c (weekly)" unit="%">
          <LineChart data={hba1c} unit="%" color="#B08585" />
        </ChartCard>
        <ChartCard title="Body fat (monthly)" unit="%">
          <LineChart data={bodyFat} unit="%" color="#8C6A3E" />
        </ChartCard>
        <ChartCard title="Muscle mass (monthly)" unit="kg">
          <LineChart data={muscle} unit="kg" color="#5C7A4F" />
        </ChartCard>
        <ChartCard title="Daily steps">
          <LineChart data={steps} color="#4A6B7A" />
        </ChartCard>
        <ChartCard title="Water (ml / day)">
          <LineChart data={water} unit="ml" color="#5C7A4F" />
        </ChartCard>
        <ChartCard title="Sleep (hours)">
          <LineChart data={sleep} unit="h" color="#5A5A6B" />
        </ChartCard>
        {insulin.some((p) => p.y != null) && (
          <ChartCard title="Insulin (units/day)">
            <LineChart data={insulin} unit="u" color="#7A5C4F" />
          </ChartCard>
        )}
      </div>
      <div className="text-xs text-[var(--lc-muted)]">
        Showing last {daily.length} daily entries · {weekly.length} weekly · {monthly.length} monthly.
      </div>
    </div>
  );
}

/* -------- Shared UI -------- */
function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 text-[var(--lc-moss)]" />}
          <h3 className="font-serif-display text-xl leading-none">{title}</h3>
        </div>
        {subtitle && <div className="text-xs text-[var(--lc-muted)]">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}
function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="eyebrow text-[var(--lc-muted)] mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </label>
      {children}
    </div>
  );
}
function NumInput({ value, onChange, step = "1", placeholder = "" }) {
  return (
    <input
      inputMode="decimal"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
    />
  );
}
function TextInput({ label, value, onChange, placeholder = "" }) {
  return (
    <div>
      {label && <label className="eyebrow text-[var(--lc-muted)] mb-2 block">{label}</label>}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
      />
    </div>
  );
}
function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative w-14 h-7 rounded-full transition-colors ${value ? "bg-[var(--lc-moss)]" : "bg-[var(--lc-line)]"}`}
    >
      <span
        className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${value ? "translate-x-7" : "translate-x-0.5"}`}
      />
    </button>
  );
}
function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-4">
      <div className="eyebrow text-[var(--lc-muted)]">{label}</div>
      <div className="font-serif-display text-2xl mt-1 leading-none">{value}</div>
    </div>
  );
}
function ChartCard({ title, unit, children }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="eyebrow text-[var(--lc-muted)]">{title}</h4>
      </div>
      {children}
    </div>
  );
}

/* -------- Sessions (client view) -------- */
function SessionsView({ token }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setSessions(await clientListSessions(token));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (s) => {
    setBusyId(s.id);
    try {
      if (s.is_rsvped || s.is_waitlisted) await clientCancelRsvp(token, s.id);
      else await clientRsvp(token, s.id);
      await load();
    } catch (err) {
      // noop — list reload will reflect current state
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-sessions">
      <div>
        <div className="eyebrow text-[var(--lc-muted)]">Upcoming sessions</div>
        <h2 className="font-serif-display text-3xl leading-tight mt-1">
          Pick a slot <span className="italic">that works for you.</span>
        </h2>
      </div>
      {loading ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions...
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-10 text-center">
          <CalendarDays className="h-7 w-7 mx-auto text-[var(--lc-muted)] mb-2" />
          <div className="font-serif-display text-xl">No sessions scheduled yet</div>
          <p className="text-sm text-[var(--lc-muted)] mt-1">
            Your coach will post new slots soon. Check back here or ping Arvind on WhatsApp.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => {
            const start = new Date(s.start_at);
            const end = new Date(s.end_at);
            const full = s.seats_left === 0 && !s.is_rsvped;
            return (
              <article
                key={s.id}
                className="bg-white border border-[var(--lc-line)] rounded-md p-5 lg:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4"
                data-testid={`session-row-${s.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif-display text-2xl leading-tight">{s.title}</h3>
                    <span className="eyebrow px-2 py-0.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm">
                      {s.type === "group" ? "Group" : "1 : 1"}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--lc-muted)]">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {start.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {" – "}
                      {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {s.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" /> {s.location}
                      </span>
                    )}
                    {s.type === "group" && (
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        {s.confirmed_count}/{s.capacity} confirmed
                      </span>
                    )}
                  </div>
                  {s.notes && (
                    <p className="mt-2 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm p-3 whitespace-pre-wrap">
                      {s.notes}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {(() => {
                    const isGroup = s.type === "group";
                    const full = s.seats_left === 0;
                    let label = "RSVP";
                    let cls = "bg-[var(--lc-ink)] text-white hover:bg-[var(--lc-moss-dark)]";
                    let disabled = busyId === s.id;
                    if (s.is_rsvped) {
                      label = "You're in · cancel?";
                      cls = "bg-white border border-[var(--lc-moss)] text-[var(--lc-moss-dark)] hover:bg-red-50 hover:border-red-400 hover:text-red-700";
                    } else if (s.is_waitlisted) {
                      label = "On waitlist · leave?";
                      cls = "bg-white border border-amber-500 text-amber-800 hover:bg-red-50 hover:border-red-400 hover:text-red-700";
                    } else if (full) {
                      if (isGroup) {
                        label = `Join waitlist · ${s.waitlist_count || 0} waiting`;
                        cls = "bg-amber-500 text-white hover:bg-amber-600";
                      } else {
                        label = "Fully booked";
                        cls = "bg-[var(--lc-line)] text-[var(--lc-muted)] cursor-not-allowed";
                        disabled = true;
                      }
                    }
                    return (
                      <button
                        disabled={disabled}
                        onClick={() => toggle(s)}
                        className={`inline-flex items-center gap-1.5 px-5 py-2.5 text-[11px] font-bold tracking-[0.14em] uppercase transition-colors ${cls}`}
                        data-testid={`session-rsvp-${s.id}`}
                      >
                        {busyId === s.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : s.is_rsvped ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : null}
                        {label}
                      </button>
                    );
                  })()}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -------- Labs (client view) -------- */
function LabsView({ token }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await clientListLabs(token);
        if (active) setLabs(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const open = async (lab) => {
    try {
      const blob = await downloadLab({ labId: lab.id, clientToken: token });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      /* no-op */
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-labs">
      <div>
        <div className="eyebrow text-[var(--lc-muted)]">Lab results</div>
        <h2 className="font-serif-display text-3xl leading-tight mt-1">
          Your medical <span className="italic">file.</span>
        </h2>
        <p className="text-sm text-[var(--lc-muted)] mt-2 max-w-xl">
          Uploaded by your partner physician. Only you and your coach can see these.
        </p>
      </div>
      {loading ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : labs.length === 0 ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-10 text-center">
          <FileText className="h-7 w-7 mx-auto text-[var(--lc-muted)] mb-2" />
          <div className="font-serif-display text-xl">No lab results yet</div>
          <p className="text-sm text-[var(--lc-muted)] mt-1">
            Your partner doctor will share results here once they're uploaded.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[var(--lc-line)] rounded-md divide-y divide-[var(--lc-line)]">
          {labs.map((l) => (
            <article key={l.id} className="p-5" data-testid={`portal-lab-row-${l.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 mt-1">
                  <FileText className="h-4 w-4 text-[var(--lc-moss-dark)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{l.title}</div>
                  <div className="text-xs text-[var(--lc-muted)] mt-0.5">
                    {new Date(l.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    {l.doctor_name ? ` · by ${l.doctor_name}` : ""}
                  </div>
                  {l.notes && (
                    <p className="mt-2 text-sm text-[var(--lc-ink)] bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm p-3 whitespace-pre-wrap">
                      {l.notes}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => open(l)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--lc-ink)] text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-ink)] hover:text-white"
                  data-testid={`portal-lab-open-${l.id}`}
                >
                  <ExternalLink className="h-3 w-3" /> Open
                </button>
              </div>
              <LabCommentThread labId={l.id} auth={{ clientToken: token }} placeholder="Ask your doctor or coach a question..." />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------- Portal shell -------- */
export default function PortalPage() {
  const { token, profile, loading, error, login, logout } = useClientAuth();
  const [tab, setTab] = useState("today");
  const [logs, setLogs] = useState({ daily: [], weekly: [], monthly: [] });
  const [loadingLogs, setLoadingLogs] = useState(false);

  const refreshLogs = async () => {
    if (!token) return;
    setLoadingLogs(true);
    try {
      const data = await clientLogs(token);
      setLogs(data);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (token && profile) refreshLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, profile?.id]);

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-[var(--lc-cream)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-muted)]" />
      </section>
    );
  }

  if (!profile) return <LoginScreen onLogin={login} error={error} />;

  return (
    <section className="bg-[var(--lc-cream)] min-h-screen pb-16">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[var(--lc-cream)]/95 backdrop-blur-md border-b border-[var(--lc-line)]">
        <div className="mx-auto max-w-[1100px] px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 leading-none">
            <span className="font-serif-display text-xl tracking-tight">LONGEV</span>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
            <span className="font-serif-display italic text-base">circle</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-semibold">{profile.name}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--lc-muted)]">
                Member
              </div>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.16em] uppercase bg-[var(--lc-ink)] text-[var(--lc-cream)] hover:bg-[var(--lc-moss-dark)] transition-colors"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto max-w-[1100px] px-5 lg:px-8">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] font-bold tracking-[0.18em] uppercase border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    active
                      ? "border-[var(--lc-ink)] text-[var(--lc-ink)]"
                      : "border-transparent text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
                  }`}
                  data-testid={`portal-tab-${t.id}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 lg:px-8 py-6 lg:py-10">
        {loadingLogs && (
          <div className="mb-4 flex items-center gap-2 text-xs text-[var(--lc-muted)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your data...
          </div>
        )}
        {tab === "today" && (
          <TodayForm token={token} logs={logs} onSaved={refreshLogs} isT1D={profile.is_t1d} />
        )}
        {tab === "weekly" && (
          <WeeklyForm token={token} logs={logs} onSaved={refreshLogs} />
        )}
        {tab === "monthly" && (
          <MonthlyForm token={token} logs={logs} onSaved={refreshLogs} />
        )}
        {tab === "sessions" && <SessionsView token={token} />}
        {tab === "labs" && <LabsView token={token} />}
        {tab === "dashboard" && <Dashboard logs={logs} />}
      </div>
    </section>
  );
}
